/**
 * «ریسک و منشأ دارایی» (Phase 5; OD-20, USDT_PROVENANCE). Open screening checks that need a human,
 * the versioned policy (conservative default until OPEN-22 — no thresholds are invented here), the
 * source allowlist, treasury acquisition records and deduplicated admin alerts. Decisions and checks
 * are append-only; nothing here moves money.
 */
import {
  AppContainer,
  Badge,
  Button,
  Card,
  DataTable,
  PageHeader,
  SelectField,
  StateView,
  TextField,
} from '@hopex/ui';
import { useState } from 'react';
import { api, dt } from '../../api.ts';
import { CommandDialog, Facts } from '../../lib/command.tsx';
import { SCREEN_RESULT } from '../../lib/crypto-model.ts';
import { Remote, useRemote } from '../../lib/remote.tsx';
import { LongId } from './shared.tsx';

interface Overview {
  policies: { version: number; thresholds: Record<string, unknown>; reason: string; at: string }[];
  open: {
    id: string;
    subjectType: string;
    subjectId: string;
    asset: string | null;
    network: string | null;
    address: string;
    txid: string | null;
    result: string;
    provider: string;
    policyVersion: number;
    categories: string[];
    score: string | null;
    at: string;
  }[];
  allowlist: {
    id: string;
    kind: string;
    name: string;
    asset: string | null;
    network: string | null;
    address: string | null;
    status: string;
    reason: string;
    version: string;
    at: string;
  }[];
  treasury: {
    id: string;
    ref: string;
    source: string;
    asset: string;
    network: string;
    amount: string;
    txid: string;
    at: string;
  }[];
}
interface Alerts {
  open: number;
  items: {
    id: string;
    kind: string;
    severity: string;
    subjectType: string;
    details: Record<string, unknown>;
    createdAt: string;
    version: string;
  }[];
}

const KIND_FA: Record<string, string> = {
  CRYPTO_DUPLICATE_TXID: 'تلاش برای ثبت TXID تکراری',
  CRYPTO_DEPOSIT_AGED_REVIEW: 'واریز رمزارز بیش از حد در انتظار بررسی',
  CRYPTO_SCREENING_BLOCKED: 'غربالگری واریز مانع اعتبار است',
  CRYPTO_WITHDRAWAL_SECURITY_HOLD: 'برداشت در توقف امنیتی',
  CRYPTO_SCREENING_UNAVAILABLE: 'غربالگری مقصد در دسترس نبود',
};

type Open =
  | { k: 'decide'; c: Overview['open'][number] }
  | { k: 'policy' }
  | { k: 'allow' }
  | { k: 'revoke'; id: string; version: string; name: string }
  | { k: 'ack'; id: string; version: string; kind: string };

export function ScreeningPage() {
  const r = useRemote<Overview>('/screening');
  const a = useRemote<Alerts>('/alerts');
  const [open, setOpen] = useState<Open | null>(null);
  const [decision, setDecision] = useState('HOLD');
  const [reviewAll, setReviewAll] = useState(true);
  const [kind, setKind] = useState<'PROVIDER' | 'WALLET'>('PROVIDER');
  const [name, setName] = useState('');
  const done = () => (setOpen(null), r.reload(), a.reload());
  return (
    <AppContainer>
      <PageHeader
        title="ریسک و منشأ دارایی"
        actions={
          <Button size="sm" variant="secondary" onPress={() => setOpen({ k: 'policy' })}>
            انتشار نسخه‌ی سیاست
          </Button>
        }
      />
      <div className="flex flex-col gap-4">
        <Remote r={a}>
          {(al) => (
            <Card className="p-5" data-admin-alerts>
              <h2 className="m-0 mb-3 flex items-center gap-2 text-[15px] font-bold">
                هشدارهای باز{' '}
                <Badge tone={al.open ? 'negative' : 'neutral'}>
                  {al.open.toLocaleString('fa-IR')}
                </Badge>
              </h2>
              {al.items.length === 0 ? (
                <StateView compact kind="empty" title="هشدار بازی وجود ندارد" />
              ) : (
                <ul className="m-0 grid list-none gap-1.5 p-0 text-[13px]">
                  {al.items.map((x) => (
                    <li
                      key={x.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line px-3 py-2"
                    >
                      <span className="flex flex-wrap items-center gap-2">
                        <Badge tone={x.severity === 'CRITICAL' ? 'negative' : 'accent'}>
                          {x.severity === 'CRITICAL' ? 'بحرانی' : 'هشدار'}
                        </Badge>
                        {KIND_FA[x.kind] ?? x.kind}
                        {typeof x.details.ref === 'string' && (
                          <bdi dir="ltr" className="font-mono text-[12px]">
                            {x.details.ref}
                          </bdi>
                        )}
                        <span className="text-fg-subtle">{dt(x.createdAt)}</span>
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onPress={() =>
                          setOpen({ k: 'ack', id: x.id, version: x.version, kind: x.kind })
                        }
                      >
                        دیدم
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </Remote>
        <Remote r={r}>
          {(d) => (
            <>
              <Card className="p-5" data-screening-open>
                <h2 className="m-0 mb-3 text-[15px] font-bold">بررسی‌های نیازمند تصمیم</h2>
                <DataTable<Overview['open'][number]>
                  label="بررسی‌های باز"
                  rows={d.open}
                  density="compact"
                  columns={[
                    {
                      id: 'subj',
                      header: 'موضوع',
                      isRowHeader: true,
                      cell: (c) =>
                        c.subjectType === 'CRYPTO_DEPOSIT'
                          ? 'واریز'
                          : c.subjectType === 'CRYPTO_WITHDRAWAL'
                            ? 'برداشت'
                            : 'خزانه',
                    },
                    {
                      id: 'net',
                      header: 'دارایی / شبکه',
                      cell: (c) => (
                        <bdi dir="ltr">
                          {c.asset} · {c.network}
                        </bdi>
                      ),
                    },
                    { id: 'addr', header: 'آدرس', cell: (c) => <LongId value={c.address} /> },
                    {
                      id: 'res',
                      header: 'نتیجه',
                      cell: (c) => (
                        <Badge tone={SCREEN_RESULT[c.result]?.tone ?? 'neutral'}>
                          {SCREEN_RESULT[c.result]?.label ?? c.result}
                        </Badge>
                      ),
                    },
                    {
                      id: 'prov',
                      header: 'ارائه‌دهنده / سیاست',
                      cell: (c) => (
                        <span className="text-[12px]">
                          {c.provider} · v{c.policyVersion}
                          {c.score ? ` · ${c.score}` : ''}
                        </span>
                      ),
                    },
                    { id: 'at', header: 'زمان', cell: (c) => dt(c.at) },
                    {
                      id: 'act',
                      header: 'تصمیم',
                      cell: (c) => (
                        <Button
                          size="sm"
                          variant="ghost"
                          onPress={() => setOpen({ k: 'decide', c })}
                        >
                          ثبت تصمیم
                        </Button>
                      ),
                    },
                  ]}
                  empty={<StateView compact kind="empty" title="موردی برای تصمیم نیست" />}
                />
              </Card>
              <div className="grid gap-4 lg:grid-cols-2">
                <Card className="p-5" data-screening-policies>
                  <h2 className="m-0 mb-3 text-[15px] font-bold">نسخه‌های سیاست</h2>
                  <ul className="m-0 grid list-none gap-1.5 p-0 text-[13px]">
                    {d.policies.map((p) => (
                      <li key={p.version} className="rounded-md border border-line px-3 py-2">
                        <span className="font-semibold">v{p.version}</span> ·{' '}
                        {p.thresholds.reviewAll
                          ? 'همه‌ی نتایج غیرمسدود: بررسی دستی'
                          : 'آستانه‌محور'}{' '}
                        · <span className="text-fg-muted">{p.reason}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
                <Card className="p-5" data-allowlist>
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <h2 className="m-0 text-[15px] font-bold">منابع مجاز تأمین</h2>
                    <Button size="sm" variant="ghost" onPress={() => setOpen({ k: 'allow' })}>
                      افزودن
                    </Button>
                  </div>
                  {d.allowlist.length === 0 ? (
                    <StateView compact kind="empty" title="منبعی ثبت نشده است" />
                  ) : (
                    <ul className="m-0 grid list-none gap-1.5 p-0 text-[13px]">
                      {d.allowlist.map((s) => (
                        <li
                          key={s.id}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line px-3 py-2"
                        >
                          <span>
                            {s.name} · {s.kind === 'PROVIDER' ? 'ارائه‌دهنده' : 'کیف پول'}{' '}
                            <Badge tone={s.status === 'ACTIVE' ? 'positive' : 'neutral'}>
                              {s.status === 'ACTIVE' ? 'فعال' : 'لغوشده'}
                            </Badge>
                          </span>
                          {s.status === 'ACTIVE' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onPress={() =>
                                setOpen({ k: 'revoke', id: s.id, version: s.version, name: s.name })
                              }
                            >
                              لغو
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {d.treasury.length > 0 && (
                    <>
                      <h3 className="m-0 mb-2 mt-4 text-[13.5px] font-bold">سوابق تأمین خزانه</h3>
                      <ul className="m-0 grid list-none gap-1 p-0 text-[12.5px]">
                        {d.treasury.map((t) => (
                          <li key={t.id}>
                            <bdi dir="ltr" className="font-mono">
                              {t.ref}
                            </bdi>{' '}
                            · {t.source} ·{' '}
                            <bdi dir="ltr">
                              {t.amount} {t.asset}
                            </bdi>{' '}
                            · {dt(t.at)}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </Card>
              </div>
            </>
          )}
        </Remote>
      </div>
      {open?.k === 'decide' && (
        <CommandDialog
          title="تصمیم غربالگری"
          run={async (reason) =>
            void (await api.post(`/screening/checks/${open.c.id}/decision`, { decision, reason }))
          }
          onClose={() => setOpen(null)}
          onDone={done}
        >
          <Facts
            rows={[
              ['نتیجه', SCREEN_RESULT[open.c.result]?.label ?? open.c.result],
              ['سیاست', `v${open.c.policyVersion}`],
            ]}
          />
          <SelectField
            label="تصمیم"
            value={decision}
            onChange={setDecision}
            options={[
              ...(open.c.result === 'REVIEW' ? [{ value: 'ACCEPT', label: 'پذیرش' }] : []),
              { value: 'HOLD', label: 'نگه‌داشتن' },
              { value: 'ESCALATE', label: 'ارجاع به فرایند انطباق' },
              { value: 'REJECT', label: 'رد' },
            ]}
          />
        </CommandDialog>
      )}
      {open?.k === 'policy' && (
        <CommandDialog
          title="انتشار نسخه‌ی تازه‌ی سیاست"
          tone="caution"
          run={async (reason) =>
            void (await api.post('/screening/policies', { reviewAll, reason }))
          }
          onClose={() => setOpen(null)}
          onDone={done}
        >
          <p className="m-0 text-[12.5px] text-fg-muted">
            آستانه‌های امتیاز و فرایند تطابق تحریم تصمیم مالک است (OPEN-22)؛ تا آن زمان حالت
            محافظه‌کارانه پیشنهاد می‌شود.
          </p>
          <label className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={reviewAll}
              onChange={(e) => setReviewAll(e.target.checked)}
            />
            همه‌ی نتایج غیرمسدود نیازمند بررسی دستی باشند
          </label>
        </CommandDialog>
      )}
      {open?.k === 'allow' && (
        <CommandDialog
          title="افزودن منبع مجاز"
          canSubmit={name.trim().length >= 2 && kind === 'PROVIDER'}
          run={async (reason) =>
            void (await api.post('/screening/allowlist', { kind, name, reason }))
          }
          onClose={() => setOpen(null)}
          onDone={done}
        >
          <SelectField
            label="نوع"
            value={kind}
            onChange={(v) => setKind(v as 'PROVIDER' | 'WALLET')}
            options={[{ value: 'PROVIDER', label: 'ارائه‌دهنده' }]}
          />
          <TextField label="نام" value={name} onChange={setName} />
        </CommandDialog>
      )}
      {open?.k === 'revoke' && (
        <CommandDialog
          title={`لغو منبع «${open.name}»`}
          tone="danger"
          run={async (reason) =>
            void (await api.post(`/screening/allowlist/${open.id}/revoke`, {
              expectedVersion: open.version,
              reason,
            }))
          }
          onClose={() => setOpen(null)}
          onDone={done}
        />
      )}
      {open?.k === 'ack' && (
        <CommandDialog
          title={KIND_FA[open.kind] ?? open.kind}
          reasonRequired={false}
          confirmLabel="ثبت مشاهده"
          run={async () =>
            void (await api.post(`/alerts/${open.id}/ack`, { expectedVersion: open.version }))
          }
          onClose={() => setOpen(null)}
          onDone={done}
        />
      )}
    </AppContainer>
  );
}
